import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { CONTACT, useLang } from "@/lib/i18n";
import { FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MATERIEL_KEYS = ["smartphones", "composants", "mixtes"];
const INITIAL = {
  entreprise: "",
  nom: "",
  phone: "",
  email: "",
  materiel: "smartphones",
  quantite: "",
  description: "",
  consent: false,
};

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.entreprise.trim() ||
      !form.nom.trim() ||
      !form.phone.trim() ||
      !form.email.trim() ||
      !form.description.trim() ||
      !form.consent
    ) {
      toast.error(c.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.sent);
      setForm(INITIAL);
    } catch (err) {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const infoRows = [
    {
      icon: Mail,
      label: c.emailLabel,
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      testid: "contact-link-email",
    },
    {
      icon: Phone,
      label: c.phoneLabel,
      value: CONTACT.phone,
      href: CONTACT.telHref,
      testid: "contact-link-phone",
    },
    {
      icon: MessageCircle,
      label: c.waLabel,
      value: CONTACT.phone,
      href: CONTACT.wa,
      testid: "contact-link-whatsapp",
      external: true,
    },
  ];

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(45%_40%_at_60%_0%,rgba(14,124,90,0.08),transparent_70%)]" />

      <section className="pb-12 pt-16 lg:pt-24" data-testid="contact-hero">
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={14}>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-brand/40" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
                {c.eyebrow}
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.15] text-ink sm:text-5xl lg:text-6xl">
              {c.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink2 sm:text-lg">{c.lead}</p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-28 pt-8" data-testid="contact-main">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
          {/* Coordonnées */}
          <div className="lg:col-span-5">
            <FadeIn>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand">
                {c.infoTitle}
              </h2>
              <div className="mt-6 overflow-hidden rounded-none border border-line bg-white shadow-card">
                {infoRows.map((r, i) => (
                  <a
                    key={r.label}
                    href={r.href}
                    target={r.external ? "_blank" : undefined}
                    rel={r.external ? "noopener noreferrer" : undefined}
                    data-testid={r.testid}
                    className={`group flex items-center gap-4 p-5 transition-colors duration-200 hover:bg-brandtint/50 ${
                      i > 0 ? "border-t border-line" : ""
                    }`}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-none bg-brandtint text-brand">
                      <r.icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-ink3">
                        {r.label}
                      </span>
                      <span className="mt-1 block break-all text-sm font-medium text-ink transition-colors group-hover:text-brand">
                        {r.value}
                      </span>
                    </span>
                  </a>
                ))}
                <div className="flex items-start gap-4 border-t border-line p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-none bg-brandtint text-brand">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-ink3">
                      {c.addressLabel}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-ink">
                      Eco Mobile Solutions
                      <br />
                      {CONTACT.address}
                    </span>
                    <span className="mt-1 block text-[10px] font-medium uppercase tracking-[0.16em] text-ink3">
                      {c.regionLabel}
                    </span>
                  </span>
                </div>
              </div>

              <a
                href={CONTACT.wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="group mt-6 flex items-center justify-between rounded-none border border-brand/30 bg-brandtint p-5 transition-colors duration-200 hover:bg-brand/10"
              >
                <span className="flex items-center gap-4">
                  <MessageCircle className="h-5 w-5 text-brand" />
                  <span className="text-sm font-semibold text-branddark">{c.waCta}</span>
                </span>
                <ArrowRight className="h-4 w-4 text-brand transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </FadeIn>
          </div>

          {/* Formulaire */}
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <form
                onSubmit={onSubmit}
                noValidate
                className="rounded-none border border-line bg-white p-6 shadow-card sm:p-10"
                data-testid="contact-form"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-ink2">
                      {c.companyLabel} <span className="text-brand">*</span>
                    </label>
                    <input
                      className="field"
                      data-testid="contact-input-entreprise"
                      value={form.entreprise}
                      onChange={set("entreprise")}
                      placeholder={c.companyPlaceholder}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-ink2">
                      {c.nameLabel} <span className="text-brand">*</span>
                    </label>
                    <input
                      className="field"
                      data-testid="contact-input-nom"
                      value={form.nom}
                      onChange={set("nom")}
                      placeholder={c.namePlaceholder}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-ink2">
                      {c.phoneFieldLabel} <span className="text-brand">*</span>
                    </label>
                    <input
                      className="field"
                      type="tel"
                      data-testid="contact-input-telephone"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder={c.phonePlaceholder}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-ink2">
                      {c.emailFieldLabel} <span className="text-brand">*</span>
                    </label>
                    <input
                      className="field"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder={c.emailPlaceholder}
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-ink2">
                      {c.materielLabel} <span className="text-brand">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2.5">
                      {MATERIEL_KEYS.map((key, i) => (
                        <button
                          key={key}
                          type="button"
                          data-testid={`contact-type-${key}`}
                          onClick={() => setForm({ ...form, materiel: key })}
                          className={`flex items-center gap-2 rounded-none border px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${
                            form.materiel === key
                              ? "border-brand bg-brandtint text-branddark"
                              : "border-line bg-white text-ink2 hover:border-brand/50 hover:text-ink"
                          }`}
                        >
                          {form.materiel === key && <Check className="h-3.5 w-3.5" />}
                          {c.materiels[i]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-ink2">
                      {c.quantityLabel}
                    </label>
                    <input
                      className="field"
                      data-testid="contact-input-quantite"
                      value={form.quantite}
                      onChange={set("quantite")}
                      placeholder={c.quantityPlaceholder}
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-ink2">
                      {c.descriptionLabel} <span className="text-brand">*</span>
                    </label>
                    <textarea
                      className="field min-h-[130px] resize-y"
                      data-testid="contact-input-description"
                      value={form.description}
                      onChange={set("description")}
                      placeholder={c.descriptionPlaceholder}
                    />
                  </div>
                </div>

                <label className="mt-6 flex cursor-pointer items-start gap-3" data-testid="contact-consent-label">
                  <input
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={form.consent}
                    onChange={set("consent")}
                    className="mt-0.5 h-4 w-4 shrink-0"
                  />
                  <span className="text-xs leading-relaxed text-ink2">{c.consent}</span>
                </label>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="group mt-8 flex w-full items-center justify-center gap-2.5 rounded-none bg-brand px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-branddark disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? c.sending : c.submit}
                  {!loading && (
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  )}
                </button>
                <p className="mt-4 text-center text-[11px] font-medium uppercase tracking-[0.14em] text-ink3">
                  {c.hint}
                </p>
              </form>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
